<script setup lang="ts">
// The reference primitive (module 02 §3.2). Folds legacy OrphexButton + Primary/Soft/Data
// into one variant-driven, copy-agnostic Button. Colours come through SEMANTIC tokens only
// (theme-aware; primary/soft/data are pixel-faithful to legacy). Label + icons via slots.
import { computed } from 'vue'

import type { ButtonProps } from './types'

const props = withDefaults(defineProps<ButtonProps>(), {
  variant: 'primary',
  size: 'md',
  type: 'button',
  disabled: false,
  loading: false,
  block: false,
  square: false,
  density: 'default',
  shape: 'default',
})

const emit = defineEmits<{ click: [event: MouseEvent] }>()

const isDisabled = computed(() => props.disabled || props.loading)
const baseClasses =
  'ui-btn inline-flex cursor-pointer select-none items-center justify-center gap-2xs whitespace-nowrap rounded-sm border font-sans font-semibold capitalize transition-all duration-150 disabled:pointer-events-none disabled:opacity-50'
const shapeClasses = computed(() => (props.shape === 'rounded' ? 'rounded-md' : undefined))
const blockClasses = computed(() => (props.block ? 'w-full' : undefined))
const squareClasses = computed(() => (props.square ? 'aspect-square' : undefined))

const paddingXClasses = computed(() => {
  if (props.square) return 'px-0'
  return props.density === 'compact' ? 'px-md' : 'px-6'
})
const sizeClasses = computed<Record<NonNullable<ButtonProps['size']>, string>>(() => ({
  xs: 'h-6 text-xs leading-4',
  sm: 'h-8 text-xs leading-4',
  md: 'h-10 text-sm leading-5',
  lg: 'h-11 text-base leading-6',
  xl: 'h-12 text-base leading-6',
}))
const variantClasses = computed<Record<NonNullable<ButtonProps['variant']>, string>>(() => ({
  primary: 'border-transparent bg-primary text-primary-fg hover:bg-primary-hover',
  soft: [
    'border-primary-subtle bg-surface text-primary-muted',
    'hover:border-primary hover:bg-surface-sunken hover:text-primary',
  ].join(' '),
  data: 'border-transparent bg-transparent text-primary-muted hover:bg-surface-sunken hover:text-primary',
  danger: 'border-transparent bg-danger text-danger-fg hover:brightness-[0.93]',
  outline: 'border-border bg-transparent text-fg hover:bg-surface-sunken',
  ghost: 'border-transparent bg-transparent text-fg hover:bg-surface-sunken',
}))

function onClick(event: MouseEvent): void {
  if (isDisabled.value) return
  emit('click', event)
}
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled"
    :aria-busy="loading || undefined"
    :class="[
      baseClasses,
      `ui-btn--${variant}`,
      `ui-btn--${size}`,
      `ui-btn--density-${density}`,
      `ui-btn--shape-${shape}`,
      variantClasses[variant],
      sizeClasses[size],
      paddingXClasses,
      shapeClasses,
      blockClasses,
      squareClasses,
      {
        'ui-btn--block': block,
        'ui-btn--square': square,
      },
    ]"
    @click="onClick"
  >
    <span
      v-if="loading"
      class="ui-btn__spinner h-[1em] w-[1em] animate-spin rounded-full border-2 border-current border-r-transparent"
      aria-hidden="true"
    />
    <slot name="start" />
    <slot />
    <slot name="end" />
  </button>
</template>
